import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Upload, Download, FileText } from 'lucide-react';
import axios from 'axios';

const DocxToPdf = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.docx')) {
        setError('Only DOCX files are supported');
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccess(false);
    }
  };

  const convertToPdf = async () => {
    if (!file) {
      setError('Please select a DOCX file');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/docx-to-pdf`,
        formData,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name.replace('.docx', '.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to convert DOCX to PDF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">DOCX to PDF Converter</h1>
        <p className="text-muted-foreground">
          Convert Word documents to PDF format instantly
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload DOCX File</CardTitle>
          <CardDescription>Select a Word document to convert</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-12 text-center">
            <input
              type="file"
              accept=".docx"
              onChange={handleFileChange}
              className="hidden"
              id="docx-upload"
            />
            <label htmlFor="docx-upload" className="cursor-pointer">
              <div className="flex flex-col items-center gap-3">
                <Upload className="h-16 w-16 text-muted-foreground" />
                <div>
                  <p className="text-lg font-medium">
                    {file ? file.name : 'Click to upload DOCX file'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Microsoft Word (.docx) format only
                  </p>
                </div>
              </div>
            </label>
          </div>

          {file && (
            <div className="bg-muted p-4 rounded-lg flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-500" />
              <div className="flex-1">
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(file.size / 1024).toFixed(2)} KB
                </p>
              </div>
            </div>
          )}

          <Button
            onClick={convertToPdf}
            disabled={!file || loading}
            className="w-full"
            size="lg"
          >
            <Download className="mr-2 h-5 w-5" />
            {loading ? 'Converting...' : 'Convert to PDF'}
          </Button>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert>
              <AlertDescription>
                PDF downloaded successfully! Check your downloads folder.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Features</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p>• Converts DOCX to PDF while preserving text formatting</p>
          <p>• Fast and secure - files are processed instantly</p>
          <p>• No file size limits</p>
          <p>• Works completely offline after page load</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DocxToPdf;
